import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { useFolhaPagamentoCadastroViewModel } from "../../viewModels/folha_pagamento_cadastro_view_model";
import { BackButton } from "../../../../../common/components/back_button";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateFolhaPagamentoCadastroColumnsDef } from "./columns";
import { isValidId } from "../../../../../common/utils/utils";
import { ItemFolhaPagamento } from "../../../domain/models/item_folha_pagamento";
import { CadastroItemFolhaPagamentoModal } from "./components/cadastro_item_folha_pagamento_modal";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";

import BottomBar from "../../../../../common/components/bottom_bar";
import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import dayjs from "dayjs";

/**
 * FolhaPagamentoCadastro
 */

export const FolhaPagamentoCadastro = () => {
  const viewModel = useFolhaPagamentoCadastroViewModel();

  const columnsDef = useMemo(() => {
    return GenerateFolhaPagamentoCadastroColumnsDef({
      subcontas: viewModel.subcontasDropdown ?? [],
      onActionChangeTitulo: viewModel.handleItemFolhaPagamentoTituloChange,
      onActionChangeSubconta: viewModel.handleItemFolhaPagamentoSubcontaChange,
      onActionChangeFormaPagamento: viewModel.handleItemFolhaPagamentoFormaPagamentoChange,
      onActionChangeValor: viewModel.handleItemFolhaPagamentoValorChange,
      onActionChangeInfoGerais: viewModel.handleItemFolhaPagamentoInfoGeraisChange,
      onActionViewItem: viewModel.handleItemFolhaPagamentoDetails,
      onActionDelete: viewModel.handleDeleteFolhaPagamentoOpenState,
    });
  }, [
    viewModel.subcontasDropdown,
    viewModel.handleItemFolhaPagamentoTituloChange,
    viewModel.handleItemFolhaPagamentoSubcontaChange,
    viewModel.handleItemFolhaPagamentoFormaPagamentoChange,
    viewModel.handleItemFolhaPagamentoValorChange,
    viewModel.handleItemFolhaPagamentoInfoGeraisChange,
    viewModel.handleItemFolhaPagamentoDetails,
    viewModel.handleDeleteFolhaPagamentoOpenState,
  ]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[12rem]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="Add" size={25} className="text-white" />}
                  onClick={() => viewModel.handleOpenCadastroItemFolhaPagamentoModal(null, true)}>
                  Incluir Pagamento
                </PrimaryButton>
              </div>

              <div className="flex">
                <BackButton
                  onClick={viewModel.handleBack} />
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-6 flex-col py-10 px-4 mb-10">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Typography className="text-primary-green">
            Folha de Pagamento
          </Typography>
        </Breadcrumbs>

        <div>
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Folha de Pagamento</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex"></div>
          </div>

          {viewModel.selectedFolhaPagamento !== undefined && (
            <div className="w-full mt-2 text-black dark:text-white">
              <span className="font-bold text-sm">{viewModel.selectedFolhaPagamento.descricao} | {viewModel.selectedFolhaPagamento.tipoFolha === 0 ? 'SIMPLES' : 'DETALHADA'}</span>
              <span className="mx-1 font-light text-sm">-</span>
              <span className="mx-1 font-light text-sm">Data de Pagamento: {dayjs(viewModel.selectedFolhaPagamento.dataReferencia).format('DD/MM/YYYY')}</span>
            </div>
          )}
        </div>
        {
          viewModel.errorMessage !== undefined ? (
            <div className="w-full">
              <AlertError
                messageErro={viewModel.errorMessage}
                onTryAgain={viewModel.handleReload} />
            </div>
          ) : (
            <>
              <Header
                idCentroCusto={viewModel.filters.idCentroCusto}
                idFuncionario={viewModel.filters.idFuncionario}
                centroCustos={viewModel.centroCustosDropdown}
                funcionarios={viewModel.funcionariosDropdown}
                onCentroCustoChange={viewModel.handleIdCentroCustoChange}
                onFuncionarioChange={viewModel.handleIdFuncionarioChange}
                onClear={viewModel.handleClearFilters} />

              <Stack spacing={0}>
                <TablePagination
                  component="div"
                  count={viewModel.pagination.totalItems}
                  page={viewModel.pagination.page - 1}
                  onPageChange={(_, p) => viewModel.handlePageChange(p)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  rowsPerPage={viewModel.pagination.rowsPerPage > 0 ? viewModel.pagination.rowsPerPage : 25}
                  onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                <StyledDataGrid
                  tableName="folha_pagamento_cadastro"
                  disableColumnMenu={false}
                  getRowId={(row: ItemFolhaPagamento) => row.idItemFolhaPagamento}
                  columns={columnsDef}
                  loading={viewModel.loading}
                  rows={viewModel.folhaPagamentoItens}
                  rowCount={viewModel.folhaPagamentoItens.length}
                  pageSizeOptions={[10, 25, 50, 100]}
                  getRowHeight={() => 'auto'}
                  paginationMode="server"
                  paginationModel={{
                    page: viewModel.pagination.page,
                    pageSize: viewModel.pagination.rowsPerPage,
                  }}
                  columnVisibilityModel={{
                    itens: viewModel.selectedFolhaPagamento && viewModel.selectedFolhaPagamento.tipoFolha === 1
                      ? true
                      : false,
                  }}
                  sx={{
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-cell": {
                      paddingTop: "0.5rem",
                      paddingBottom: "0.5rem",
                    }
                  }} />

                <TablePagination
                  component="div"
                  count={viewModel.pagination.totalItems}
                  page={viewModel.pagination.page - 1}
                  onPageChange={(_, p) => viewModel.handlePageChange(p)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  rowsPerPage={viewModel.pagination.rowsPerPage > 0 ? viewModel.pagination.rowsPerPage : 25}
                  onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

              </Stack>
            </>
          )
        }
      </div>

      <BottomBar>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isValidId(viewModel.idFolhaPagamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={() => viewModel.handleOpenChangeHistory(null, null)}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-4">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={viewModel.handleBack}>
                Cancelar
              </h1>
            </div>
            <div className="w-[130px]">
              {viewModel.saving
                ? (
                  <PrimaryButton>
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    onClick={viewModel.handleSubmitChanges}
                    startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                    Salvar
                  </PrimaryButton>
                )}
            </div>
          </div>
        </div>
      </BottomBar>

      {/**
        * Modals
        */}

      <CadastroItemFolhaPagamentoModal
        open={viewModel.openCadastroItemFolhaPagamentoModal}
        saving={viewModel.saving}
        idFuncionarioUsuario={viewModel.funcionarioInfo !== null ? viewModel.funcionarioInfo.idFuncionario : null}
        selectedFolhaPagamento={viewModel.selectedFolhaPagamento ?? null}
        centroCustos={viewModel.centroCustosDropdown ?? []}
        funcionarios={viewModel.funcionariosResponsavelDropdown ?? []}
        funcionariosCadastro={viewModel.funcionariosCadastroDropdown ?? []}
        subcontas={viewModel.subcontasDropdown ?? []}
        reactHookForm={viewModel.hookForm}
        onSubmit={viewModel.handleSubmitItemFolhaPagamento}
        onClose={() => viewModel.handleOpenCadastroItemFolhaPagamentoModal(null, false)}
      />

      <DeleteDialog
        open={viewModel.openDeleteItemFolhaPagamentoModal}
        saving={viewModel.saving}
        onClose={() => viewModel.handleDeleteFolhaPagamentoOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleConfirmarDeletarItemFolhaPagamento} />

      {/**
        * Modal historico de alterações.
        */}

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory} />
    </>
  );
}